import ExcelJS from 'exceljs';
import { getSalesPriceList, bulkUpdatePriceList } from '../services/salesService';
import { cellText } from './classificationExcel';

export const PRICE_COL = {
  id: 'ID',
  sku: 'SKU (referencia)',
  name: 'Nombre',
  unit: 'Unidad',
};

const SINGLE_PRICE_HEADER = 'Precio (S/)';

const HEADER_FILL = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFDBEAFE' },
};

function downloadBuffer(buffer, filename) {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function styleHeaderRow(row, labels, fill = HEADER_FILL) {
  labels.forEach((label, i) => {
    const c = row.getCell(i + 1);
    c.value = label;
    c.font = { bold: true };
    c.fill = fill;
  });
}

export function priceHeaderForType(type) {
  return `${type.name} (S/)`;
}

export function buildPriceListHeaders(salePriceTypes = []) {
  const base = [PRICE_COL.id, PRICE_COL.sku, PRICE_COL.name, PRICE_COL.unit];
  if (salePriceTypes.length > 0) {
    return [...base, ...salePriceTypes.map(priceHeaderForType)];
  }
  return [...base, SINGLE_PRICE_HEADER];
}

function productToRow(product, headers, salePriceTypes) {
  const row = {
    [PRICE_COL.id]: product.id,
    [PRICE_COL.sku]: product.sku || '',
    [PRICE_COL.name]: product.name || '',
    [PRICE_COL.unit]: product.unit_abbreviation || product.unit_of_measure || '',
  };

  if (salePriceTypes.length > 0) {
    salePriceTypes.forEach((type) => {
      const key = priceHeaderForType(type);
      const val = product.type_prices?.[String(type.id)] ?? product.type_prices?.[type.id];
      row[key] = val != null ? Number(val) : '';
    });
  } else {
    row[SINGLE_PRICE_HEADER] = product.price != null ? Number(product.price) : '';
  }

  return headers.map((h) => row[h] ?? '');
}

export async function fetchPriceListCatalog(pointOfSaleId) {
  const products = [];
  let salePriceTypes = [];
  let page = 1;
  let lastPage = 1;

  do {
    const res = await getSalesPriceList(Number(pointOfSaleId), '', page, 200, 'prices');
    if (!res?.success) {
      throw new Error(res?.message || 'No se pudo cargar la lista de precios del punto de venta.');
    }
    if (page === 1 && res.settings?.sale_price_types) {
      salePriceTypes = res.settings.sale_price_types;
    }
    products.push(...(res.data || []));
    lastPage = res.meta?.last_page ?? 1;
    page += 1;
  } while (page <= lastPage);

  return { products, salePriceTypes };
}

export async function downloadPriceListSheet(products = [], salePriceTypes = [], posLabel = '') {
  const headers = buildPriceListHeaders(salePriceTypes);
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Lista de precios', { views: [{ state: 'frozen', ySplit: 3 }] });
  const lastCol = String.fromCharCode('A'.charCodeAt(0) + headers.length - 1);

  ws.mergeCells(`A1:${lastCol}1`);
  ws.getCell('A1').value =
    'Lista de precios: edite los montos en soles (S/). No modifique ID ni SKU. Deje vacío un precio para no cambiarlo. Vuelva a importar el archivo editado.';
  ws.getCell('A1').alignment = { wrapText: true, vertical: 'middle' };
  ws.getRow(1).height = 48;

  ws.mergeCells(`A2:${lastCol}2`);
  ws.getCell('A2').value = `${products.length} producto(s) habilitado(s)${posLabel ? ` · ${posLabel}` : ''}`;
  ws.getCell('A2').font = { italic: true, color: { argb: 'FF4B5563' } };

  styleHeaderRow(ws.getRow(3), headers);

  const sorted = [...products].sort((a, b) =>
    String(a?.sku || '').localeCompare(String(b?.sku || ''), 'es', { sensitivity: 'base' }),
  );
  sorted.forEach((product) => {
    ws.addRow(productToRow(product, headers, salePriceTypes));
  });

  const colWidths = [{ width: 8 }, { width: 22 }, { width: 36 }, { width: 10 }];
  const priceCols = Math.max(1, salePriceTypes.length || 1);
  for (let i = 0; i < priceCols; i += 1) {
    colWidths.push({ width: 16 });
  }
  ws.columns = colWidths;

  const buf = await wb.xlsx.writeBuffer();
  const date = new Date().toISOString().split('T')[0];
  const safePos = String(posLabel || 'pos')
    .replace(/[^\w\-]+/g, '_')
    .slice(0, 40);
  downloadBuffer(buf, `lista_precios_${safePos}_${date}.xlsx`);
}

function headerMapFromRow(row) {
  const map = {};
  row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    const t = cellText(cell);
    if (t) map[t] = colNumber;
  });
  return map;
}

function findHeaderRow(sheet, requiredLabels) {
  const maxScan = Math.min(30, sheet.rowCount || 30);
  for (let r = 1; r <= maxScan; r++) {
    const row = sheet.getRow(r);
    const map = headerMapFromRow(row);
    if (requiredLabels.every((label) => map[label] != null)) {
      return { rowIndex: r, colMap: map };
    }
  }
  return null;
}

function getLastUsedRow(sheet) {
  let last = 0;
  sheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    if (rowNumber > last) last = rowNumber;
  });
  return last;
}

async function loadFirstSheet(file) {
  const buf = await file.arrayBuffer();
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buf);
  return wb.worksheets[0] || null;
}

function buildPriceColumnMap(colMap, salePriceTypes) {
  const columns = [];
  if (salePriceTypes.length > 0) {
    salePriceTypes.forEach((type) => {
      const label = priceHeaderForType(type);
      if (colMap[label] != null) {
        columns.push({ label, typeId: type.id, col: colMap[label] });
      }
    });
  } else if (colMap[SINGLE_PRICE_HEADER] != null) {
    columns.push({ label: SINGLE_PRICE_HEADER, typeId: null, col: colMap[SINGLE_PRICE_HEADER] });
  }
  return columns;
}

function parsePriceCell(raw) {
  const text = String(raw ?? '').trim();
  if (text === '') return { empty: true };
  const normalized = text.replace(',', '.');
  const n = parseFloat(normalized);
  if (!Number.isFinite(n) || n < 0) return { invalid: true };
  return { value: Math.round(n * 100) / 100 };
}

function getExistingPrice(product, typeId, salePriceTypes) {
  if (salePriceTypes.length > 0 && typeId) {
    const v = product.type_prices?.[String(typeId)] ?? product.type_prices?.[typeId];
    return v != null ? Number(v) : null;
  }
  if (product.price != null) return Number(product.price);
  const defaultType = salePriceTypes.find((t) => t.is_default);
  if (defaultType) {
    const v =
      product.type_prices?.[String(defaultType.id)] ?? product.type_prices?.[defaultType.id];
    return v != null ? Number(v) : null;
  }
  return null;
}

function formatMoney(n) {
  if (n == null) return '—';
  return new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(n);
}

export async function analyzePriceListImport(file, { pointOfSaleId }) {
  const sheet = await loadFirstSheet(file);
  if (!sheet) {
    return { success: false, fatalMessage: 'El archivo no tiene hojas.' };
  }

  let products = [];
  let salePriceTypes = [];
  try {
    const catalog = await fetchPriceListCatalog(pointOfSaleId);
    products = catalog.products;
    salePriceTypes = catalog.salePriceTypes;
  } catch (err) {
    return { success: false, fatalMessage: err?.message || 'No se pudieron cargar los productos del POS.' };
  }

  const priceHeaders =
    salePriceTypes.length > 0
      ? salePriceTypes.map(priceHeaderForType)
      : [SINGLE_PRICE_HEADER];
  const requiredHeaders = [PRICE_COL.id, ...priceHeaders];
  const found = findHeaderRow(sheet, [PRICE_COL.id, priceHeaders[0]]);
  if (!found) {
    return {
      success: false,
      fatalMessage: `Faltan encabezados obligatorios. Debe existir al menos: ${requiredHeaders.join(', ')}.`,
    };
  }

  const priceColumns = buildPriceColumnMap(found.colMap, salePriceTypes);
  if (!priceColumns.length) {
    return {
      success: false,
      fatalMessage: `No se encontraron columnas de precio. Use la planilla descargada (${priceHeaders.join(', ')}).`,
    };
  }

  const productById = new Map(products.map((p) => [Number(p.id), p]));
  const { rowIndex: headerRow, colMap } = found;
  const last = getLastUsedRow(sheet);
  const previewRows = [];
  const validTasks = [];

  for (let r = headerRow + 1; r <= last; r++) {
    const row = sheet.getRow(r);
    const idRaw = cellText(row.getCell(colMap[PRICE_COL.id]));
    const skuRaw = colMap[PRICE_COL.sku] ? cellText(row.getCell(colMap[PRICE_COL.sku])) : '';
    const nameRaw = colMap[PRICE_COL.name] ? cellText(row.getCell(colMap[PRICE_COL.name])) : '';

    const priceValues = priceColumns.map((col) => ({
      ...col,
      raw: cellText(row.getCell(col.col)),
    }));

    const hasAnyData =
      idRaw ||
      skuRaw ||
      nameRaw ||
      priceValues.some((p) => p.raw !== '');

    if (!hasAnyData) {
      previewRows.push({ excelRow: r, status: 'empty', cols: {}, issue: '' });
      continue;
    }

    const id = parseInt(String(idRaw).trim(), 10);
    const colsPreview = {
      id: idRaw,
      sku: skuRaw,
      nombre: nameRaw,
      precios: priceValues.map((p) => `${p.label}: ${p.raw || '—'}`).join(' · '),
    };

    if (!Number.isFinite(id) || id < 1) {
      previewRows.push({
        excelRow: r,
        status: 'invalid',
        cols: colsPreview,
        issue: 'ID de producto inválido.',
      });
      continue;
    }

    const existing = productById.get(id);
    if (!existing) {
      previewRows.push({
        excelRow: r,
        status: 'invalid',
        cols: colsPreview,
        issue: `El producto ID ${id} no está habilitado en este punto de venta.`,
      });
      continue;
    }

    const rowChanges = [];
    const rowIssues = [];
    let hasValid = false;
    let hasUnchanged = false;
    let allEmpty = true;

    priceValues.forEach((pv) => {
      const parsed = parsePriceCell(pv.raw);
      if (parsed.empty) return;
      allEmpty = false;

      if (parsed.invalid) {
        rowIssues.push(`${pv.label}: valor inválido.`);
        return;
      }

      const current = getExistingPrice(existing, pv.typeId, salePriceTypes);
      if (current !== null && Math.abs(current - parsed.value) < 0.001) {
        hasUnchanged = true;
        return;
      }

      hasValid = true;
      rowChanges.push({
        label: pv.label,
        from: current,
        to: parsed.value,
        payload: {
          product_id: id,
          price: parsed.value,
          sales_price_type_id: pv.typeId,
        },
      });
    });

    if (rowIssues.length > 0) {
      previewRows.push({
        excelRow: r,
        status: 'invalid',
        cols: {
          ...colsPreview,
          sku: existing.sku || skuRaw,
          nombre: existing.name || nameRaw,
        },
        issue: rowIssues.join(' '),
      });
      continue;
    }

    if (allEmpty) {
      previewRows.push({
        excelRow: r,
        status: 'unchanged',
        cols: {
          ...colsPreview,
          sku: existing.sku || skuRaw,
          nombre: existing.name || nameRaw,
        },
        issue: 'Sin precios editados; se omite.',
      });
      continue;
    }

    if (!hasValid) {
      previewRows.push({
        excelRow: r,
        status: 'unchanged',
        cols: {
          ...colsPreview,
          sku: existing.sku || skuRaw,
          nombre: existing.name || nameRaw,
        },
        issue: hasUnchanged ? 'Sin cambios respecto al sistema.' : 'Sin precios editados; se omite.',
      });
      continue;
    }

    const cambios = rowChanges
      .map((c) => `${c.label}: ${formatMoney(c.from)} → ${formatMoney(c.to)}`)
      .join(' · ');

    previewRows.push({
      excelRow: r,
      status: 'valid',
      cols: {
        ...colsPreview,
        sku: existing.sku || skuRaw,
        nombre: existing.name || nameRaw,
        cambios,
      },
      issue: '',
    });

    rowChanges.forEach((c) => {
      validTasks.push({
        excelRow: r,
        payload: c.payload,
      });
    });
  }

  return {
    success: true,
    previewRows,
    validTasks,
    validCount: validTasks.length,
    emptyCount: previewRows.filter((x) => x.status === 'empty').length,
    invalidCount: previewRows.filter((x) => x.status === 'invalid').length,
    unchangedCount: previewRows.filter((x) => x.status === 'unchanged').length,
  };
}

export async function executePriceListImport(validTasks, pointOfSaleId) {
  const items = validTasks.map(({ payload }) => payload);
  if (!items.length) {
    return { updated: 0, errors: [] };
  }

  try {
    const res = await bulkUpdatePriceList({
      point_of_sale_id: Number(pointOfSaleId),
      items,
    });
    if (!res?.success) {
      return {
        updated: 0,
        errors: (res.errors || []).map((e) => ({
          row: e.row ?? e.product_id,
          message: e.message || res.message,
        })),
      };
    }
    return {
      updated: res.data?.updated ?? res.updated ?? items.length,
      errors: (res.errors || []).map((e) => ({
        row: e.row ?? e.product_id,
        message: e.message,
      })),
    };
  } catch (err) {
    const d = err.response?.data;
    const message = d?.message || err.message || 'Error al guardar';
    return {
      updated: 0,
      errors: [{ row: '-', message }],
    };
  }
}

export function summarizePriceListResult(result) {
  const parts = [];
  if (result.updated > 0) parts.push(`${result.updated} precio(s) actualizado(s)`);
  if (result.errors?.length) parts.push(`${result.errors.length} error(es)`);
  if (!parts.length) return 'No se realizaron cambios.';
  let msg = parts.join(' · ');
  if (result.errors?.length) {
    const detail = result.errors
      .slice(0, 5)
      .map((e) => `Fila ${e.row}: ${e.message}`)
      .join(' | ');
    msg += `. ${detail}`;
  }
  return msg;
}
